import { Check, Clock, Pencil, Users, X } from "lucide-react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface AgentUser {
  user_id: string;
  granted_by?: string;
  expires_at?: string | null;
  profile?: {
    full_name?: string;
    email?: string;
    avatar_url?: string;
  };
}

interface Props {
  users: AgentUser[];
  isLoading: boolean;
  authUserId?: string;
  editingExpiry: Record<string, string>;
  isUpdatePending: boolean;
  isDeletePending: boolean;
  onExpiryEdit: (userId: string, current: string | null) => void;
  onExpirySave: (userId: string) => void;
  onExpiryCancel: (userId: string) => void;
  onExpiryChange: (userId: string, value: string) => void;
  onRemove: (userId: string) => void;
}

const AgentUsersList = ({
  users,
  isLoading,
  authUserId,
  editingExpiry,
  isUpdatePending,
  isDeletePending,
  onExpiryEdit,
  onExpirySave,
  onExpiryCancel,
  onExpiryChange,
  onRemove,
}: Props) => {
  if (isLoading) return <Card className="p-8 text-center text-gray-400">Loading...</Card>;

  if (users.length === 0) {
    return (
      <Card className="p-8 text-center text-gray-400 text-sm">
        No users have been granted direct access to this genie
      </Card>
    );
  }

  return (
    <Card className="divide-y divide-gray-100">
      {users.map((u) => {
        const profile = u.profile;
        const isExpired = u.expires_at && new Date(u.expires_at) < new Date();
        const isTeamGrant = u.granted_by && u.granted_by !== authUserId;
        return (
          <div key={u.user_id} className="flex items-center justify-between p-4 gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center shrink-0">
                  <Users className="h-4 w-4 text-genie-primary" />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    <CopyableEmail email={profile?.full_name || profile?.email || u.user_id} />
                  </p>
                  {isTeamGrant && (
                    <Badge className="bg-genie-primary/10 text-genie-primary text-xs shrink-0">
                      Team
                    </Badge>
                  )}
                </div>
                {profile?.full_name && (
                  <CopyableEmail email={profile.email} className="text-xs text-gray-500" textClassName="truncate" />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {editingExpiry[u.user_id] !== undefined ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    value={editingExpiry[u.user_id]}
                    onChange={(e) => onExpiryChange(u.user_id, e.target.value)}
                    className="h-7 text-xs w-36 px-2"
                  />
                  <Button
                    size="sm"
                    className="h-7 px-2 bg-gradient-to-r from-genie-primary to-genie-secondary text-white"
                    onClick={() => onExpirySave(u.user_id)}
                    disabled={isUpdatePending}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() => onExpiryCancel(u.user_id)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <button
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-genie-primary transition-colors group"
                  onClick={() => onExpiryEdit(u.user_id, u.expires_at ?? null)}
                >
                  <Clock className="h-3 w-3" />
                  <span className={isExpired ? "text-red-400" : ""}>
                    {u.expires_at
                      ? isExpired
                        ? "Expired"
                        : new Date(u.expires_at).toLocaleDateString()
                      : "No expiry"}
                  </span>
                  <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="text-red-500 hover:text-red-600 hover:border-red-300"
                onClick={() => onRemove(u.user_id)}
                disabled={isDeletePending}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        );
      })}
    </Card>
  );
};

export default AgentUsersList;
